import React from "react";
import "./AccountScreen.css";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCreditCard,
  faShieldHalved,
  faLocationDot,
  faEnvelope,
  faPhone,
  faTrashCan,
  faPlus,
  faEye,
  faEyeSlash,
  faKey,
  faUserGear,
} from "@fortawesome/free-solid-svg-icons";
import { API_IP, STRIPE_KEY } from "../global_variables";
import NewCardModal from "./AccountComponents/NewCardModal";
import PromptModal from "./ReuseComponent/PromptModal";

const stripePromise = loadStripe(STRIPE_KEY);

const TABS = [
  { id: "overview", label: "Overview", icon: faUser },
  { id: "payment", label: "Payment", icon: faCreditCard },
  { id: "security", label: "Security", icon: faShieldHalved },
];

const CARD_BRAND_STYLES = {
  visa: { gradient: "linear-gradient(135deg, #1a1f71 0%, #3b5bd9 100%)", label: "VISA" },
  mastercard: { gradient: "linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)", label: "MasterCard" },
  amex: { gradient: "linear-gradient(135deg, #2e77bc 0%, #6cb4e4 100%)", label: "AMEX" },
  discover: { gradient: "linear-gradient(135deg, #ff6000 0%, #ffb347 100%)", label: "Discover" },
  unionpay: { gradient: "linear-gradient(135deg, #e21836 0%, #00447c 50%, #007b3a 100%)", label: "UnionPay" },
  jcb: { gradient: "linear-gradient(135deg, #0e4c96 0%, #d8232a 50%, #1e8e3e 100%)", label: "JCB" },
  diners: { gradient: "linear-gradient(135deg, #004a97 0%, #0079be 100%)", label: "Diners" },
  default: { gradient: "linear-gradient(135deg, #1d4e89 0%, #6e8c6e 100%)", label: "CARD" },
};

const getCardStyle = (brand) => {
  if (!brand) return CARD_BRAND_STYLES.default;
  const key = brand.toLowerCase().replace(/\s/g, "");
  return CARD_BRAND_STYLES[key] || CARD_BRAND_STYLES.default;
};

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: "", color: "#e0e0e0" };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const levels = [
    { label: "Too weak", color: "#c62828" },
    { label: "Weak", color: "#d97706" },
    { label: "Fair", color: "#b88a3e" },
    { label: "Good", color: "#8faf8f" },
    { label: "Strong", color: "#6e8c6e" },
    { label: "Excellent", color: "#5a7a5a" },
  ];
  return { score, ...levels[score] };
};

const formatDate = (date) => {
  try {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

/** Shared card header: circular icon wrapper + title + subtitle, matching the
 *  section-card pattern used across the iCare Village app. */
const CardHead = ({ icon, title, subtitle, children }) => (
  <header className="panel-card-head">
    <div className="panel-card-head-main">
      <span className="panel-card-icon" aria-hidden="true">
        <FontAwesomeIcon icon={icon} />
      </span>
      <div>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </div>
    {children && <div className="panel-card-head-action">{children}</div>}
  </header>
);

const UserAccountScreen = ({ setSelectedSection }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [isNewCardModalVisible, setIsNewCardModalVisible] = useState(false);
  const [clientSecret, setClientSecret] = useState("");

  const [activeTab, setActiveTab] = useState("overview");
  const [cardToDelete, setCardToDelete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingCard, setDeletingCard] = useState(false);
  const [cardActionError, setCardActionError] = useState(null);

  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const [accountInfo, setAccountInfo] = useState(null);

  const EMPTY_ADDRESS = {
    line1: "",
    city: "",
    region: "",
    country: "",
    postal: "",
  };
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [billingDraft, setBillingDraft] = useState(EMPTY_ADDRESS);
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingMsg, setBillingMsg] = useState(null);

  const tabRefs = useRef([]);

  const storedUsername = localStorage.getItem("username") || "Guest";
  const username = accountInfo?.username || storedUsername;
  const userEmail = accountInfo?.email || "—";
  const phoneNumber = accountInfo?.phone_number
    ? String(accountInfo.phone_number)
    : "—";
  // Start of the current browser session — used only to label the live session
  // row below. We don't fabricate a "last login" the backend hasn't given us.
  const sessionStart = useMemo(() => new Date().toISOString(), []);

  const toggleNewCardModal = () => setIsNewCardModalVisible(!isNewCardModalVisible);
  const toggleRefresh = () => setIsRefresh(!isRefresh);
  const toggleAlert = () => setIsAlertVisible(!isAlertVisible);

  const fetchClientSecret = useCallback(async () => {
    try {
      const bearerToken = localStorage.getItem("token");
      const response = await fetch(`${API_IP}/api/create-setup-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      const data = await response.json();
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setCards(data.listCards || []);
        if (data.address) {
          const mapped = {
            line1: data.address.line1 || "",
            city: data.address.city || "",
            region: data.address.state || "",
            country: data.address.country || "",
            postal: data.address.postal_code || "",
          };
          setBillingAddress(mapped);
          setBillingDraft(mapped);
        }
        setLoading(false);
      } else {
        console.error("Failed to get client secret:", data.error);
      }
    } catch (error) {
      console.error("Error fetching client secret:", error);
    }
  }, [isRefresh]);

  useEffect(() => {
    fetchClientSecret();
  }, [fetchClientSecret]);

  useEffect(() => {
    const fetchAccountInfo = async () => {
      try {
        const bearerToken = localStorage.getItem("token");
        if (!bearerToken) return;
        const response = await fetch(`${API_IP}/api/setting`, {
          method: "GET",
          headers: { Authorization: `Bearer ${bearerToken}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        setAccountInfo(data);
      } catch (err) {
        console.error("Failed to load account info:", err);
      }
    };
    fetchAccountInfo();
  }, []);

  const strength = getPasswordStrength(newPw);

  const handleTabKeyDown = (e, index) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" ? 1 : -1;
    const next = (index + dir + TABS.length) % TABS.length;
    setActiveTab(TABS[next].id);
    tabRefs.current[next]?.focus();
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwSubmitting) return;
    if (!currentPw || !newPw || !confirmPw) {
      setPasswordMsg({ type: "error", text: "Please fill in all password fields." });
      return;
    }
    if (newPw !== confirmPw) {
      setPasswordMsg({ type: "error", text: "New password and confirmation do not match." });
      return;
    }
    if (strength.score < 3) {
      setPasswordMsg({ type: "error", text: "Please choose a stronger password." });
      return;
    }

    setPwSubmitting(true);
    setPasswordMsg(null);
    try {
      const bearerToken = localStorage.getItem("token");
      const response = await fetch(`${API_IP}/api/account/password`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.ok) {
        setPasswordMsg({
          type: "success",
          text: (data && data.message) || "Password updated successfully.",
        });
        setCurrentPw("");
        setNewPw("");
        setConfirmPw("");
        setShowCurrentPw(false);
        setShowNewPw(false);
      } else {
        const errText =
          (data && data.error) ||
          (response.status === 401
            ? "Current password is incorrect."
            : "Failed to update password. Please try again.");
        setPasswordMsg({ type: "error", text: errText });
      }
    } catch (err) {
      console.error("Change password error:", err);
      setPasswordMsg({
        type: "error",
        text: "Unable to reach the server. Please try again.",
      });
    } finally {
      setPwSubmitting(false);
    }
  };

  const requestDeleteCard = (card) => {
    setCardToDelete(card);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteCard = async () => {
    if (!cardToDelete || deletingCard) return;
    setDeletingCard(true);
    setCardActionError(null);
    try {
      const bearerToken = localStorage.getItem("token");
      const response = await fetch(
        `${API_IP}/api/payment-method/${encodeURIComponent(cardToDelete.id)}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken}`,
          },
        },
      );

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        setCardActionError(
          (data && data.error) || "Failed to remove card. Please try again.",
        );
        return;
      }

      const removedId = cardToDelete.id;
      setCards((prev) => prev.filter((c) => c.id !== removedId));
      setCardToDelete(null);
      setDeleteConfirmOpen(false);
      toggleRefresh();
    } catch (err) {
      console.error("Remove card error:", err);
      setCardActionError("Unable to reach the server. Please try again.");
    } finally {
      setDeletingCard(false);
    }
  };

  const saveBillingAddress = async () => {
    if (savingBilling) return;
    const { line1, city, region, country, postal } = billingDraft;
    if (!line1 || !city || !region || !country || !postal) {
      setBillingMsg({
        type: "error",
        text: "Please fill in all billing address fields.",
      });
      return;
    }

    setSavingBilling(true);
    setBillingMsg(null);
    try {
      const bearerToken = localStorage.getItem("token");
      const response = await fetch(`${API_IP}/api/billing-address`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify(billingDraft),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        setBillingMsg({
          type: "error",
          text:
            (data && data.error) ||
            "Failed to save billing address. Please try again.",
        });
        return;
      }

      const saved = data && data.address
        ? {
            line1: data.address.line1 || "",
            city: data.address.city || "",
            region: data.address.state || "",
            country: data.address.country || "",
            postal: data.address.postal_code || "",
          }
        : billingDraft;
      setBillingAddress(saved);
      setBillingDraft(saved);
      setEditingBilling(false);
      setBillingMsg({
        type: "success",
        text: (data && data.message) || "Billing address saved.",
      });
    } catch (err) {
      console.error("Save billing address error:", err);
      setBillingMsg({
        type: "error",
        text: "Unable to reach the server. Please try again.",
      });
    } finally {
      setSavingBilling(false);
    }
  };

  const cancelBillingEdit = () => {
    if (savingBilling) return;
    setBillingDraft(billingAddress);
    setBillingMsg(null);
    setEditingBilling(false);
  };

  const hasBillingAddress = Boolean(
    billingAddress.line1 ||
      billingAddress.city ||
      billingAddress.region ||
      billingAddress.country ||
      billingAddress.postal,
  );

  if (!clientSecret) {
    return (
      <div className="loading-container">
        <img src={require("../images/loading_icon.gif")} alt="Loading your account" />
      </div>
    );
  }

  const options = {
    appearance: { theme: "stripe" },
    clientSecret: clientSecret,
  };

  const initials = username
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      <Elements stripe={stripePromise} options={options}>
        <NewCardModal
          showModal={isNewCardModalVisible}
          handleClose={toggleNewCardModal}
          toggleAlert={toggleAlert}
          toggleRefresh={toggleRefresh}
          clientSecret={clientSecret}
        />
        <PromptModal
          title={"Your card has been successfully saved"}
          type={"alert"}
          showModal={isAlertVisible}
          handleClose={toggleAlert}
        />
        <PromptModal
          title={`Remove card ending in ${cardToDelete?.card?.last4 || ""}?`}
          type={"binary"}
          showModal={deleteConfirmOpen}
          isLoading={deletingCard}
          handleClose={() => {
            if (deletingCard) return;
            setDeleteConfirmOpen(false);
            setCardToDelete(null);
            setCardActionError(null);
          }}
          handleConsent={confirmDeleteCard}
        />
      </Elements>

      <div className="screen-container account-screen">
        {/* Screen header — matches the section-header pattern used app-wide */}
        <header className="acct-screen-header">
          <span className="acct-screen-icon" aria-hidden="true">
            <FontAwesomeIcon icon={faUserGear} />
          </span>
          <div>
            <h1 className="acct-screen-title">Account</h1>
            <p className="acct-screen-subtitle">
              Manage your profile, payments, and security — all in one place.
            </p>
          </div>
        </header>

        {/* Profile summary */}
        <section className="acct-profile">
          <div className="acct-avatar">{initials || "U"}</div>
          <div className="acct-profile-text">
            <h2 className="acct-profile-name">{username}</h2>
            <div className="acct-chips">
              <span className="acct-chip acct-chip-active">
                <span className="acct-dot" /> Active account
              </span>
              <span className="acct-chip">
                <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" /> {userEmail}
              </span>
              {phoneNumber !== "—" && (
                <span className="acct-chip">
                  <FontAwesomeIcon icon={faPhone} aria-hidden="true" /> {phoneNumber}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className="acct-tabs" role="tablist" aria-label="Account sections">
          {TABS.map((tab, index) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                ref={(el) => (tabRefs.current[index] = el)}
                id={`tab-${tab.id}`}
                role="tab"
                aria-selected={selected}
                aria-controls={`panel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                className={`acct-tab ${selected ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(e) => handleTabKeyDown(e, index)}
              >
                <FontAwesomeIcon icon={tab.icon} className="acct-tab-icon" aria-hidden="true" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {activeTab === "overview" && (
          <div className="tab-panel" role="tabpanel" id="panel-overview" aria-labelledby="tab-overview">
            {/* Status bento — at-a-glance tiles that drill into each detail tab */}
            <div className="acct-bento">
              <button
                type="button"
                className="acct-status-card"
                onClick={() => setActiveTab("payment")}
              >
                <span className="acct-status-icon">
                  <FontAwesomeIcon icon={faCreditCard} aria-hidden="true" />
                </span>
                <span className="acct-status-value">{cards.length}</span>
                <span className="acct-status-label">
                  {cards.length === 1 ? "Saved card" : "Saved cards"}
                </span>
                <span className="acct-status-cta">Manage payments</span>
              </button>

              <button
                type="button"
                className="acct-status-card"
                onClick={() => setActiveTab("payment")}
              >
                <span className="acct-status-icon">
                  <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                </span>
                <span className="acct-status-value">
                  {hasBillingAddress ? "On file" : "Not set"}
                </span>
                <span className="acct-status-label">Billing address</span>
                <span className="acct-status-cta">
                  {hasBillingAddress ? "Edit billing" : "Add billing"}
                </span>
              </button>

              <button
                type="button"
                className="acct-status-card"
                onClick={() => setActiveTab("security")}
              >
                <span className="acct-status-icon">
                  <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
                </span>
                <span className="acct-status-value">Protected</span>
                <span className="acct-status-label">Password login</span>
                <span className="acct-status-cta">Review security</span>
              </button>
            </div>

            <section className="panel-card">
              <CardHead icon={faUser} title="Personal information" subtitle="Your profile details" />
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Username</span>
                  <span className="info-value">{username}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{userEmail}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone</span>
                  <span className="info-value">{phoneNumber}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Account status</span>
                  <span className="status-badge status-active">● Active</span>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === "payment" && (
          <div className="tab-panel" role="tabpanel" id="panel-payment" aria-labelledby="tab-payment">
            <section className="panel-card">
              <CardHead
                icon={faCreditCard}
                title="Payment methods"
                subtitle="Securely manage your saved cards"
              >
                <button className="primary-button" onClick={toggleNewCardModal}>
                  <FontAwesomeIcon icon={faPlus} aria-hidden="true" /> Add new card
                </button>
              </CardHead>

              {cardActionError && (
                <div className="form-message error" style={{ marginBottom: 12 }}>
                  {cardActionError}
                </div>
              )}

              {cards.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <FontAwesomeIcon icon={faCreditCard} aria-hidden="true" />
                  </div>
                  <h3>No cards saved yet</h3>
                  <p>Add a payment method to speed up checkout and booking.</p>
                  <button className="primary-button" onClick={toggleNewCardModal}>
                    Add your first card
                  </button>
                </div>
              ) : (
                <div className="cards-grid">
                  {cards.map((card) => {
                    const style = getCardStyle(card.card.brand);
                    return (
                      <div key={card.id} className="credit-card">
                        <div
                          className="credit-card-visual"
                          style={{ background: style.gradient }}
                        >
                          <div className="credit-card-top">
                            <div className="card-chip" />
                          </div>
                          <div className="credit-card-number">
                            •••• •••• •••• {card.card.last4}
                          </div>
                          <div className="credit-card-bottom">
                            <div>
                              <div className="cc-label">Expires</div>
                              <div className="cc-value">
                                {String(card.card.exp_month).padStart(2, "0")}/
                                {String(card.card.exp_year).slice(-2)}
                              </div>
                            </div>
                            <div className="cc-brand">{style.label}</div>
                          </div>
                        </div>
                        <div className="credit-card-actions">
                          <span className="muted-text">
                            {card.card.brand
                              ? `${style.label} ending in ${card.card.last4}`
                              : `Card ending in ${card.card.last4}`}
                          </span>
                          <button
                            className="icon-button danger"
                            onClick={() => requestDeleteCard(card)}
                            aria-label={`Remove card ending in ${card.card.last4}`}
                          >
                            <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="panel-card">
              <CardHead
                icon={faLocationDot}
                title="Billing address"
                subtitle="Used for invoices and receipts"
              >
                {!editingBilling && hasBillingAddress && (
                  <button
                    className="ghost-button"
                    onClick={() => {
                      setBillingMsg(null);
                      setEditingBilling(true);
                    }}
                  >
                    Edit
                  </button>
                )}
              </CardHead>
              {billingMsg && !editingBilling && (
                <div
                  className={`form-message ${billingMsg.type}`}
                  style={{ marginBottom: 12 }}
                >
                  {billingMsg.text}
                </div>
              )}
              {!editingBilling ? (
                hasBillingAddress ? (
                  <div className="billing-address-display">
                    <p>{billingAddress.line1}</p>
                    <p>
                      {billingAddress.city}
                      {billingAddress.city && billingAddress.region ? ", " : ""}
                      {billingAddress.region}
                    </p>
                    <p>
                      {billingAddress.country} {billingAddress.postal}
                    </p>
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                    </div>
                    <h3>No billing address saved</h3>
                    <p>Add a billing address for invoices and receipts.</p>
                    <button
                      className="primary-button"
                      onClick={() => {
                        setBillingMsg(null);
                        setEditingBilling(true);
                      }}
                    >
                      Add billing address
                    </button>
                  </div>
                )
              ) : (
                <div className="billing-form">
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="billing-line1">Street address</label>
                      <input
                        id="billing-line1"
                        type="text"
                        value={billingDraft.line1}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, line1: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-row two-col">
                    <div className="form-field">
                      <label htmlFor="billing-city">City</label>
                      <input
                        id="billing-city"
                        type="text"
                        value={billingDraft.city}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, city: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="billing-region">Region / state</label>
                      <input
                        id="billing-region"
                        type="text"
                        value={billingDraft.region}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, region: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-row two-col">
                    <div className="form-field">
                      <label htmlFor="billing-country">Country</label>
                      <input
                        id="billing-country"
                        type="text"
                        value={billingDraft.country}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, country: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="billing-postal">Postal code</label>
                      <input
                        id="billing-postal"
                        type="text"
                        value={billingDraft.postal}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, postal: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  {billingMsg && (
                    <div className={`form-message ${billingMsg.type}`}>
                      {billingMsg.text}
                    </div>
                  )}
                  <div className="form-actions">
                    <button
                      className="ghost-button"
                      onClick={cancelBillingEdit}
                      disabled={savingBilling}
                    >
                      Cancel
                    </button>
                    <button
                      className="primary-button"
                      onClick={saveBillingAddress}
                      disabled={savingBilling}
                    >
                      {savingBilling ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {activeTab === "security" && (
          <div className="tab-panel" role="tabpanel" id="panel-security" aria-labelledby="tab-security">
            <section className="panel-card">
              <CardHead
                icon={faKey}
                title="Change password"
                subtitle="Choose a strong, unique password to protect your account."
              />
              <form className="password-form" onSubmit={handleChangePassword}>
                <div className="form-field">
                  <label htmlFor="current-pw">Current password</label>
                  <div className="input-with-action">
                    <input
                      id="current-pw"
                      type={showCurrentPw ? "text" : "password"}
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      className="input-action"
                      aria-label={showCurrentPw ? "Hide password" : "Show password"}
                      onClick={() => setShowCurrentPw(!showCurrentPw)}
                    >
                      <FontAwesomeIcon
                        icon={showCurrentPw ? faEyeSlash : faEye}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
                <div className="form-row two-col">
                  <div className="form-field">
                    <label htmlFor="new-pw">New password</label>
                    <div className="input-with-action">
                      <input
                        id="new-pw"
                        type={showNewPw ? "text" : "password"}
                        value={newPw}
                        onChange={(e) => setNewPw(e.target.value)}
                        placeholder="At least 8 characters"
                      />
                      <button
                        type="button"
                        className="input-action"
                        aria-label={showNewPw ? "Hide password" : "Show password"}
                        onClick={() => setShowNewPw(!showNewPw)}
                      >
                        <FontAwesomeIcon
                          icon={showNewPw ? faEyeSlash : faEye}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    {newPw && (
                      <div className="strength-meter">
                        <div className="strength-bar-track">
                          <div
                            className="strength-bar-fill"
                            style={{
                              width: `${(strength.score / 5) * 100}%`,
                              background: strength.color,
                            }}
                          />
                        </div>
                        <span className="strength-label" style={{ color: strength.color }}>
                          {strength.label}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="form-field">
                    <label htmlFor="confirm-pw">Confirm new password</label>
                    <input
                      id="confirm-pw"
                      type={showNewPw ? "text" : "password"}
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      placeholder="Re-enter new password"
                    />
                  </div>
                </div>
                {passwordMsg && (
                  <div className={`form-message ${passwordMsg.type}`}>{passwordMsg.text}</div>
                )}
                <div className="form-actions">
                  <button
                    type="submit"
                    className="primary-button"
                    disabled={pwSubmitting}
                  >
                    {pwSubmitting ? "Updating…" : "Update password"}
                  </button>
                </div>
              </form>
            </section>

            <section className="panel-card">
              <CardHead
                icon={faShieldHalved}
                title="Sign-in activity"
                subtitle="Your current session on this device."
              />
              <ul className="activity-list">
                <li className="activity-item">
                  <div className="activity-dot" />
                  <div className="activity-main">
                    <div className="activity-title">Current session · Web</div>
                    <div className="activity-sub">{formatDate(sessionStart)} · This device</div>
                  </div>
                  <span className="status-badge status-active">Active</span>
                </li>
              </ul>
            </section>
          </div>
        )}
      </div>
    </>
  );
};

export default UserAccountScreen;
